import { useCallback, useEffect, useRef, useState } from "react";
import { getLogServicesCatalogos, listarLogServices, inactivarLogServices } from "../../api/logServices/logServicesApi";

const INITIAL_FILTERS = {
  sedes: [],
  jobCodes: [],
  codigoError: "",
  identificacion: "",
  fechaDesde: "",
  fechaHasta: "",
};

const INITIAL_STATE = {
  catalogos: { sedes: [], jobCodes: [], codigosError: [] },
  loadingCatalogos: true,
  logs: [],
  pagination: { page: 1, perPage: 50, total: 0, totalPages: 0 },
  loadingLogs: false,
  filtros: { ...INITIAL_FILTERS },
};

export default function useLogServices() {
  const [state, setState] = useState(INITIAL_STATE);
  const catalogosLoadedRef = useRef(false);
  const currentPageRef = useRef(1);
  const filtrosRef = useRef({ ...INITIAL_FILTERS });
  const requestSeqRef = useRef(0);

  useEffect(() => {
    filtrosRef.current = state.filtros;
  }, [state.filtros]);

  const cargarCatalogos = useCallback(async () => {
    if (catalogosLoadedRef.current) return;
    catalogosLoadedRef.current = true;
    setState((prev) => ({ ...prev, loadingCatalogos: true }));
    try {
      const res = await getLogServicesCatalogos();
      const data = res?.success ? res.data : { sedes: [], jobCodes: [], codigosError: [] };
      setState((prev) => ({
        ...prev,
        loadingCatalogos: false,
        catalogos: {
          sedes: data.sedes || [],
          jobCodes: data.jobCodes || [],
          codigosError: data.codigosError || [],
        },
      }));
    } catch (err) {
      console.error("Error cargando catalogos logServices", err);
      setState((prev) => ({
        ...prev,
        loadingCatalogos: false,
        catalogos: { sedes: [], jobCodes: [], codigosError: [] },
      }));
    }
  }, []);

  const cargarLogs = useCallback(async (opts = {}) => {
    const seq = ++requestSeqRef.current;
    const page = opts.page ?? currentPageRef.current;
    const force = !!opts.force;

    const filtrosAplicados = force ? opts.filtros ?? filtrosRef.current : filtrosRef.current;

    setState((prev) => ({ ...prev, loadingLogs: true }));
    try {
      const res = await listarLogServices({ ...filtrosAplicados, page });
      if (seq !== requestSeqRef.current) return;

      const data = res?.success ? res.data : [];
      const pagination = res?.pagination || { page, per_page: 50, total: 0, total_pages: 0 };
      currentPageRef.current = page;

      setState((prev) => ({
        ...prev,
        loadingLogs: false,
        logs: data,
        pagination: {
          page: pagination.page ?? page,
          perPage: pagination.per_page ?? 50,
          total: pagination.total ?? 0,
          totalPages: pagination.total_pages ?? 0,
        },
      }));
    } catch (err) {
      console.error("Error cargando logs", err);
      if (seq !== requestSeqRef.current) return;
      setState((prev) => ({
        ...prev,
        loadingLogs: false,
        logs: [],
        pagination: { page, perPage: 50, total: 0, totalPages: 0 },
      }));
    }
  }, []);

  const actualizarFiltro = useCallback((campo, valor) => {
    filtrosRef.current = { ...filtrosRef.current, [campo]: valor };
    setState((prev) => ({
      ...prev,
      filtros: { ...prev.filtros, [campo]: valor },
    }));
  }, []);

  const aplicarFiltros = useCallback(() => {
    currentPageRef.current = 1;
    cargarLogs({ page: 1, force: true, filtros: filtrosRef.current });
  }, [cargarLogs]);

  const limpiarFiltros = useCallback(() => {
    filtrosRef.current = { ...INITIAL_FILTERS };
    setState((prev) => ({ ...prev, filtros: { ...INITIAL_FILTERS } }));
    currentPageRef.current = 1;
    cargarLogs({ page: 1, force: true, filtros: filtrosRef.current });
  }, [cargarLogs]);

  const cambiarPagina = useCallback((pagina) => {
    cargarLogs({ page: pagina });
  }, [cargarLogs]);

  const inactivarLogs = useCallback(async (ids) => {
    const payload = Array.isArray(ids) ? { ids } : { id: ids };
    const res = await inactivarLogServices(payload);
    if (res?.success) {
      await cargarLogs({ page: currentPageRef.current, force: true });
      return res;
    }
    throw new Error(res?.message || "No se pudo inactivar el registro");
  }, [cargarLogs]);

  return {
    state,
    actions: {
      cargarCatalogos,
      cargarLogs,
      actualizarFiltro,
      aplicarFiltros,
      limpiarFiltros,
      cambiarPagina,
      inactivarLogs,
    },
  };
}
